<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { api, ExtendedRequestConfig } from "../../../api/Api";
import IconComponent from "../common/IconComponent.vue";
import FilePreview from "./FilePreview.vue";
import { Loader2 } from "lucide-vue-next";
import {
  isEmpty,
  printPDFBlob,
  setDefaultImage,
} from "../../../utils/CommonFunctions";
import IconPdf from "./UploadIcons/IconPdf.vue";
import IconText from "./UploadIcons/IconText.vue";
import IconXml from "./UploadIcons/IconXml.vue";
import IconWord from "./UploadIcons/IconWord.vue";
import IconHtml from "./UploadIcons/IconHtml.vue";
import IconExcel from "./UploadIcons/IconExcel.vue";

const props = defineProps<{
  modelValue?: string | string[];
  //accept?: string
  label: string;
  maxSize?: number; // in MB
  multiple?: boolean;
  image?: boolean;
  path: string; // API endpoint for file upload
  title?: string;
  className?: string;
  readonly?: boolean;
  ocr?: boolean;
  ocrLength?: number | string;
  ocrMatch?: string;
  currency?: string;
  error?: string | null;
  size?: "mini" | "small" | "medium" | "large";
  local?: boolean;
  returnItem?: any;
}>();

const acceptedFileTypes = ref("");

const emit = defineEmits<{
  (e: "update:modelValue", value: string | string[]): void;
  (e: "upload-file", response: File, item: any): void;
  (e: "upload-success", response: any, item: any): void;
  (e: "upload-error", error: any): void;
  (e: "upload-progress", progress: number): void;
  (e: "ocr-read", reference: string, amount: string, file: string): void;
  (e: "remove"): void;
}>();

interface ocrData {
  reference: string;
  amount: string;
}
const handleOcr = (data: ocrData, file: string) => {
  emit("ocr-read", data.reference, data.amount, file);
};
const dragActive = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const uploadedFiles = ref<{ file: File; preview: string; name: string }[]>([]);
const uploadProgress = ref<number>(0);
const isUploading = ref(false);
const uploadError = ref<string | null>(null);
const showPreviewDialog = ref(false);
const selectedFile = ref<{ file: File; preview: string; name: string } | null>(
  null
);

const handleDrag = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.type === "dragenter" || e.type === "dragover") {
    dragActive.value = true;
  } else if (e.type === "dragleave") {
    dragActive.value = false;
  }
};

const handleDrop = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
  dragActive.value = false;

  if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
    handleFiles(Array.from(e.dataTransfer.files));
  }
};

const handleChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    handleFiles(Array.from(target.files));
  }
};

const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      resolve("");
    }
  });
};

const uploadFile = async (file: File) => {
  isUploading.value = true;
  uploadError.value = null;
  uploadProgress.value = 0;

  try {
    const formData = new FormData();
    formData.append("file", file);
    if (props.path) {
      formData.append("path", props.path);
      formData.append("readOcr", props.ocr ? "true" : "false");
      formData.append("ocrMatch", props.ocrMatch || "");
      formData.append("ocrLength", String(props.ocrLength || ""));
      formData.append("currency", String(props.currency || ""));
    }

    const response = await api.post("clients/common/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: { total: number; loaded: number }) => {
        if (progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          uploadProgress.value = progress;
          emit("upload-progress", progress);
        }
      },
      skipLoading: true,
    } as ExtendedRequestConfig);

    if (response.success == true) {
      if (props.ocr && response.ocr) {
        handleOcr(response.ocr, response.data);
      }
      emit("upload-success", response.data, props.returnItem);
      return response.data;
    }
  } catch (error) {
    uploadError.value = "Failed to upload file";
    emit("upload-error", error);
    return null;
  } finally {
    isUploading.value = false;
  }
};

const handleFiles = async (files: File[]) => {
  if (props.local) {
    emit("upload-success", files, props.returnItem);
    return;
  }
  const acceptedTypes =
    acceptedFileTypes.value.split(",").map((type) => type.trim()) || [];
  const validFiles = files.filter((file) => {
    const isValidType =
      acceptedTypes.length === 0 ||
      acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return file.name.toLowerCase().endsWith(type.toLowerCase());
        }
        return file.type.match(new RegExp(type.replace("*", ".*")));
      });
    const isValidSize =
      !props.maxSize || file.size <= props.maxSize * 1024 * 1024;
    return isValidType && isValidSize;
  });

  if (validFiles.length > 0) {
    if (props.multiple) {
      const uploadResults = await Promise.all(
        validFiles.map(async (file) => {
          const filename = await uploadFile(file);
          if (filename) {
            const preview = await createImagePreview(file);
            return { file, preview, name: filename };
          }
          return null;
        })
      );

      const successfulUploads = uploadResults.filter(
        (result): result is NonNullable<typeof result> => result !== null
      );
      uploadedFiles.value = props.multiple
        ? [...uploadedFiles.value, ...successfulUploads]
        : successfulUploads.length > 0
        ? [successfulUploads[0]]
        : [];

      // Emit filenames
      const filenames = uploadedFiles.value.map((f) => f.name);
      emit(
        "update:modelValue",
        props.multiple ? filenames : filenames[0] || ""
      );
    } else {
      // Single file upload
      const filename = await uploadFile(validFiles[0]);
      if (filename) {
        const preview = await createImagePreview(validFiles[0]);
        uploadedFiles.value = [
          { file: validFiles[0], preview, name: filename },
        ];
        emit("update:modelValue", filename);
      }
    }
  }
};

const openFileDialog = () => {
  fileInput.value?.click();
};

const removeFile = async (index: number) => {
  uploadedFiles.value.splice(index, 1);
  const filenames = uploadedFiles.value.map((f) => f.name);
  emit("update:modelValue", props.multiple ? filenames : filenames[0] || "");
  emit("remove");
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Watch for modelValue changes from parent
watch(
  () => props.modelValue,
  (newValue) => {
    if (props.readonly && newValue) {
      getImage();
    } else if (
      !newValue ||
      (Array.isArray(newValue) && newValue.length === 0)
    ) {
      uploadedFiles.value = [];
    }
  }
);

onMounted(async () => {
  if (props.image) {
    acceptedFileTypes.value = "image/*";
  } else {
    acceptedFileTypes.value =
      "image/*,.xlsx,.xls,application/pdf,text/plain,text/xml,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/html,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  const response = await api.post("image/data",
      {
        filename: Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue
      }, {
        hideError: true
      }
    );
  loading.value = false;
  imageData.value = response.data;
});

//if there is a image already
const imageData = ref("");
const loading = ref(true);

const getImage = async () => {
  try {
    if (
      props.modelValue == "" ||
      props.modelValue == null ||
      props.modelValue == undefined
    ) {
      imageData.value = "";
      return;
    }
    const response = await api.post("image/data",
      {
        filename: Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue
      }, {
        hideError: true
      }
    );
    imageData.value = response.success && response.data ? response.data : "";
  } catch (e) {
    imageData.value = "";
  } finally {
    loading.value = false;
  }
};

const handleRemove = () => {
  imageData.value = "";
  emit("update:modelValue", "");
  emit("remove");
};

const viewFile = (index: number) => {
  selectedFile.value = uploadedFiles.value[index];
  //TODO
  console.log("viewFile to be Fixed");
  //selectedFile.value.name = 'https://raw.githubusercontent.com/mozilla/pdf.js/ba2edeae/web/compressed.tracemonkey-pldi-09.pdf'
  console.log(selectedFile.value);
  showPreviewDialog.value = true;
};



const viewPdfFile = async () => {
  const filename = Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '';
  const response = await api.post("image/data",
      {
        filename: filename
      }, {
        hideError: true,
        skipLoading: true
      }
    );
    /*if (resp.success == true) {
      console.log(resp.data);
    }

  let response = await getBase64Image(props.src || '');*/
  loading.value = false
  imageData.value = response.success &&  response.data ? response.data : null
  console.log('viewPdfFile imageData', imageData.value);
  printPDFBlob(imageData.value);
};

const getFileExtension = (filename: string): string => {
  if (!filename) return "";
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

const getFileType = (filename: string): string => {
  const extension = getFileExtension(filename);
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];
  const documentExtensions = ["pdf", "doc", "docx"];
  const spreadsheetExtensions = ["xls", "xlsx"];
  const textExtensions = ["txt", "csv"];
  const xmlExtensions = ["xml"];
  const htmlExtensions = ["html", "htm"];
  
  if (imageExtensions.includes(extension)) return "image";
  if (extension === "pdf") return "application/pdf";
  if (textExtensions.includes(extension)) return "text/plain";
  if (xmlExtensions.includes(extension)) return "text/xml";
  if (documentExtensions.includes(extension)) return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (htmlExtensions.includes(extension)) return "text/html";
  if (spreadsheetExtensions.includes(extension)) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  
  return "";
};

const showPreview = ref(false);
</script>

<template>
  <div
    :class="{
      'space-y-4': props.size == 'large',
      'space-y-2': props.size == 'medium',
      'max-h-[100px]': props.size == 'mini',
      'max-h-[150px]': props.size == 'small',
      'max-h-[200px]': props.size == 'medium',
    }"
  >
    <div>
      <label
        class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        {{ label }}
      </label>
      <template slot="right-header"></template>
    </div>
    <!-- Upload Area -->
    <div
      v-if="
        (!uploadedFiles.length && !props.modelValue && !props.readonly) ||
        (multiple && !props.readonly)
      "
      @dragenter="handleDrag"
      @dragover="handleDrag"
      @dragleave="handleDrag"
      @drop="handleDrop"
      class="relative border-2 border-dashed rounded-lg text-center transition-colors"
      :class="[
        dragActive
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500',
        error ? 'border-red-300 dark:border-red-700' : '',
        props.size == 'large'
          ? 'p-6'
          : props.size == 'medium'
          ? 'p-4'
          : props.size == 'small'
          ? 'p-2'
          : '',
      ]"
    >
      <!-- Hidden file input -->
      <input
        ref="fileInput"
        type="file"
        :accept="image == true ? 'image/*' : acceptedFileTypes"
        class="hidden"
        @change="handleChange"
        :multiple="multiple"
      />

      <!-- Upload icon -->
      <div
        :class="{
          'mb-4': props.size == 'large',
          'mb-2': props.size == 'medium',
        }"
        v-if="props.size != 'mini'"
      >
        <span class="text-4xl">📄</span>
      </div>

      <!-- Upload text -->
      <p
        class="text-sm text-gray-600 dark:text-gray-400"
        :class="{ 'mb-2': props.size != 'mini' }"
      >
        <span
          class="font-medium text-blue-600 dark:text-blue-400"
          :class="{ 'text-xs': props.size == 'mini' }"
        >
          {{ props.size == "mini" ? "Upload" : "Click to Upload" }}
        </span>
        or {{ props.size == "mini" ? "Drop" : "Drag and Drop" }} files here
      </p>
      <p class="text-xs text-gray-500 dark:text-gray-400">
        {{ props.size != "mini" ? "Accepted files:" : "" }}
        {{ image ? "Only Images" : "PDF, Excel, Word" }}
        {{ props.maxSize ? ` (Max ${props.maxSize}MB)` : "" }}
      </p>

      <!-- Click overlay -->
      <button
        type="button"
        @click="openFileDialog"
        class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">
      {{ error }}
    </p>

    <!-- Upload Progress -->
    <div
      v-if="isUploading"
      class="bg-gray-50 dark:bg-gray-900 rounded-lg"
      :class="{ 'p-4': props.size !== 'mini' }"
    >
      <div
        class="flex items-center gap-2"
        :class="{ 'mb-2': props.size !== 'mini' }"
      >
        <div
          class="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
        >
          <div
            class="h-full bg-blue-600 transition-all duration-300"
            :style="{ width: `${uploadProgress}%` }"
          ></div>
        </div>
        <span class="text-sm text-gray-600 dark:text-gray-400"
          >{{ uploadProgress }}%</span
        >
      </div>
    </div>

    <!-- Upload Error -->
    <div
      v-if="uploadError"
      class="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg"
    >
      {{ uploadError }}
    </div>

    <!-- File List -->
    <div v-if="uploadedFiles.length > 0 && !props.image" class="space-y-2">
      <div
        v-for="(file, index) in uploadedFiles"
        :key="index"
        class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
      >
        <div class="flex items-center gap-3 min-w-0">
          <!-- Image Preview or File Icon -->
          <div
            v-if="file.preview"
            class="w-12 h-12 rounded overflow-hidden flex-shrink-0"
          >
            <img
              :src="file.preview"
              class="w-full h-full object-cover"
              :alt="file.file.name"
              @error="setDefaultImage"
            />
          </div>
          <div
            v-else
            class="w-12 h-12 flex items-center justify-center text-2xl flex-shrink-0"
          >
            <IconPdf v-if="file.file.type == 'application/pdf'" />
            <IconText v-else-if="file.file.type == 'text/plain'" />
            <IconXml v-else-if="file.file.type == 'text/xml'" />
            <IconWord
              v-else-if="
                file.file.type ==
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              "
            />
            <IconHtml v-else-if="file.file.type == 'text/html'" />
            <IconExcel
              v-else-if="
                file.file.type ==
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              "
            />
            <span v-else>{{
              file.file.type.startsWith("image/") ? "🖼️" : "📄"
            }}</span>
          </div>

          <div class="flex flex-col min-w-0">
            <div class="max-w-[200px] overflow-hidden">
              <span
                class="text-sm font-medium text-gray-900 dark:text-white truncate block"
                >{{ file.file.name }}</span
              >
            </div>
            <span class="text-xs text-gray-500 dark:text-gray-400">{{
              formatFileSize(file.file.size)
            }}</span>
          </div>
        </div>
        <div>
          <button
            @click="viewFile(index)"
            class="p-1 bg-green-500 rounded-full text-white hover:bg-green-800 flex-shrink-0 mr-1"
          >
            <IconComponent name="Eye" class="w-4 h-4" />
          </button>
          <button
            @click="removeFile(index)"
            class="p-1 bg-red-500 rounded-full text-white hover:bg-red-800 flex-shrink-0"
          >
            <IconComponent name="Trash" class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
    <!-- if many images -->
    <div v-if="uploadedFiles.length > 0 && props.image" class="flex">
      <div
        v-for="(file, index) in uploadedFiles"
        :key="index"
        class="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded-lg group relative"
        :class="{ 'p-3 w-20 mr-1': props.size != 'mini' }"
      >
        <div class="flex items-center gap-3 min-w-0">
          <!-- Image Preview or File Icon -->
          <div
            v-if="file.preview"
            class="w-12 h-12 rounded overflow-hidden flex-shrink-0"
          >
            <img
              :src="file.preview"
              class="w-full h-full object-cover"
              :alt="file.file.name"
              @error="setDefaultImage"
            />
          </div>
        </div>
        <button
          @click="removeFile(index)"
          class="absolute top-0 right-0 p-1 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
        >
          <IconComponent name="Trash" class="w-4 h-4" />
        </button>
      </div>
    </div>
    <!-- if single image -->
    <div
      v-else-if="props.image || getFileType(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '') == 'image'"
      class="group relative"
      :class="{ 'w-20': props.size != 'mini' }"
    >
      <div v-if="loading" class="flex items-center justify-center">
        <Loader2 class="w-6 h-6 animate-spin" />
      </div>
      <template v-else-if="props.readonly || !isEmpty(props.modelValue)">
        <div
          v-if="showPreview"
          class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          @click="showPreview = false"
        >
          <img :src="imageData" class="max-w-full max-h-full" />
        </div>
        <img
          v-else
          :src="imageData"
          :alt="props.title"
          :class="{
            'max-h-[40px]': props.size == 'mini',
            'max-h-[80px]': props.size == 'small',
          }"
          class="cursor-pointer rounded"
          @error="setDefaultImage"
          @click="showPreview = true"
        />
        <button
          v-if="!props.readonly"
          @click="handleRemove"
          class="absolute top-0 right-0 p-1 bg-red-500 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
        >
          <IconComponent name="Trash" class="w-4 h-4" />
        </button>
      </template>
    </div>
    <div
      v-else-if="props.readonly && !isEmpty(props.modelValue)"
      class="group relative"
      :class="{ 'w-20': props.size != 'mini' }"
    >
    <IconPdf v-if="getFileType(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '') == 'application/pdf'" @click="viewPdfFile"/>
            <IconText v-else-if="getFileType(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '') == 'text/plain'" />
            <IconXml v-else-if="getFileType(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '') == 'text/xml'" />
            <IconWord
              v-else-if="
                getFileType(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '') ==
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              "
            />
            <IconHtml v-else-if="getFileType(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '') == 'text/html'" />
            <IconExcel
              v-else-if="
                getFileType(Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue || '') ==
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              "
            />
            <span v-else>{{
              "🖼️"
            }}</span>
    </div>
    <FilePreview
      :show="showPreviewDialog"
      :file="selectedFile?.name"
      @close="showPreviewDialog = false"
    />
  </div>
</template>
